import React from 'react';
import { BookOpen, Target, MessageSquare, BarChart3, ArrowRight, RotateCcw } from 'lucide-react';

const steps = [
  {
    icon: BookOpen,
    label: 'Read',
    title: 'Daily Lesson',
    description: 'One focused chapter. Real scenarios. 20 minutes.',
    detail: 'Each day tackles a specific leadership skill with examples drawn from IT management.',
  },
  {
    icon: Target,
    label: 'Apply',
    title: 'Try It at Work',
    description: 'A practical action to use that day.',
    detail: 'Not homework — a real thing to try in your next meeting, 1:1, or project check-in.',
  },
  {
    icon: MessageSquare,
    label: 'Reflect',
    title: 'Talk to Your Companion',
    description: 'Process what happened with your AI coach.',
    detail: 'Your companion remembers context, asks the right questions, and never judges.',
  },
  {
    icon: BarChart3,
    label: 'Measure',
    title: 'See Your Growth',
    description: 'Patterns, capabilities, and progress — tracked.',
    detail: 'Over time, your reflections build a Living CV of demonstrated leadership evidence.',
  },
];

const SystemFlow = ({ series }) => {
  const accent = series?.accentColor || '#DC2626';

  return (
    <section className="py-20 bg-white relative overflow-hidden" id="system">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: accent }}>
            How It Works
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3">
            One cycle. Every day. <span style={{ color: accent }}>Compounding.</span>
          </h2>
          <p className="text-base text-slate-500 max-w-xl mx-auto">
            Leadership isn&apos;t a course you finish — it&apos;s a system you run. Each day repeats the cycle, and every cycle builds on the last.
          </p>
        </div>

        {/* Desktop: horizontal flow */}
        <div className="hidden lg:block max-w-5xl mx-auto mb-12">
          <div className="grid grid-cols-4 gap-0 relative">
            {/* Connecting line behind */}
            <div className="absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-slate-200 z-0" />

            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.label} className="relative flex flex-col items-center text-center z-10">
                  {/* Icon circle */}
                  <div
                    className="w-24 h-24 rounded-2xl flex items-center justify-center mb-5 shadow-sm border transition-transform hover:scale-110 duration-200"
                    style={{
                      backgroundColor: `${accent}08`,
                      borderColor: `${accent}20`,
                    }}
                  >
                    <Icon className="w-10 h-10" style={{ color: accent }} />
                  </div>

                  {/* Step label */}
                  <span
                    className="text-xs font-bold uppercase tracking-widest mb-2"
                    style={{ color: accent }}
                  >
                    {step.label}
                  </span>

                  <h3 className="text-base font-semibold text-slate-900 mb-1.5">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed max-w-[200px]">{step.description}</p>

                  {/* Arrow between steps */}
                  {i < steps.length - 1 && (
                    <div className="absolute top-12 -right-3 z-20">
                      <ArrowRight className="w-5 h-5 text-slate-300" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Repeat loop indicator */}
          <div className="flex justify-center mt-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-200 text-sm text-slate-500">
              <RotateCcw className="w-4 h-4" style={{ color: accent }} />
              <span>Repeat daily. Each cycle builds on the last.</span>
            </div>
          </div>
        </div>

        {/* Mobile: vertical flow */}
        <div className="lg:hidden max-w-md mx-auto mb-8">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={step.label} className="relative">
                <div className="flex gap-4 items-start">
                  {/* Icon + connector */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm border"
                      style={{
                        backgroundColor: `${accent}08`,
                        borderColor: `${accent}20`,
                      }}
                    >
                      <Icon className="w-7 h-7" style={{ color: accent }} />
                    </div>
                    {i < steps.length - 1 && (
                      <div className="w-0.5 h-8 bg-slate-200 my-2" />
                    )}
                  </div>

                  {/* Text */}
                  <div className="pt-1 pb-6">
                    <span
                      className="text-xs font-bold uppercase tracking-widest"
                      style={{ color: accent }}
                    >
                      {step.label}
                    </span>
                    <h3 className="text-base font-semibold text-slate-900 mt-0.5">{step.title}</h3>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex justify-center mt-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-200 text-sm text-slate-500">
              <RotateCcw className="w-4 h-4" style={{ color: accent }} />
              <span>Repeat daily</span>
            </div>
          </div>
        </div>

        {/* Detail cards — bottom row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {steps.map((step) => (
            <div
              key={step.label}
              className="rounded-xl bg-slate-50 border border-slate-100 p-4"
            >
              <p className="text-sm text-slate-600 leading-relaxed">{step.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SystemFlow;
